import React, { useEffect, useRef, useState } from 'react';
import { Coordinates } from '../../Models/Models';

/** Crop window without the transport flag; structurally accepts Models.Coordinates. */
export interface CropRect {
    left: number;
    up: number;
    right: number;
    down: number;
}

interface CropBoxProps {
    imageUrl: string;
    imageAlt?: string;
    /** Locked aspect ratio of the window as width / height (e.g. 4 / 3). */
    aspect: number;
    /** Smallest allowed window width in original-image pixels; height follows the aspect. */
    minWidth?: number;
    /** Starting window in original-image pixels; falls back to the largest centred window. */
    initial?: CropRect | null;
    /** Fires with original-image pixel coordinates whenever the window settles. */
    onChange: (coords: Coordinates) => void;
}

/** Window state in original-image pixels. */
type Box = { x: number; y: number; w: number; h: number };

type Corner = 'nw' | 'ne' | 'sw' | 'se';

type DragState =
    | { mode: 'move'; grabX: number; grabY: number }
    | { mode: Corner; anchorX: number; anchorY: number };

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(v, hi));

const VEIL = 'rgba(24, 24, 27, 0.55)';
const EDGE = 'rgba(255, 255, 255, 0.95)';

/**
 * Aspect-locked crop editor: drag the window to move it, pull a corner grip to
 * resize, double-click to take the largest possible window. The window lives in
 * original-image pixels, so layout resizes never corrupt the crop.
 */
const CropBox: React.FC<CropBoxProps> = ({ imageUrl, imageAlt = '', aspect, minWidth = 1, initial, onChange }) => {
    const stageRef = useRef<HTMLDivElement>(null);
    const imgRef = useRef<HTMLImageElement>(null);
    const dragRef = useRef<DragState | null>(null);
    const [natural, setNatural] = useState({ w: 0, h: 0 });
    const [displayWidth, setDisplayWidth] = useState(0);
    const [box, setBox] = useState<Box | null>(null);
    const [interacting, setInteracting] = useState(false);
    const [failed, setFailed] = useState(false);

    const scale = natural.w > 0 && displayWidth > 0 ? displayWidth / natural.w : 0;
    const ready = box !== null && scale > 0;

    // Largest window with the target aspect that the source can hold
    const maxW = natural.w > 0 ? Math.min(natural.w, natural.h * aspect) : 0;
    const minW = natural.w > 0 ? Math.min(minWidth, maxW) : minWidth;
    const sourceTooSmall = natural.w > 0 && maxW < minWidth - 0.5;

    const largestCentredBox = (nw: number, nh: number): Box => {
        const w = Math.min(nw, nh * aspect);
        const h = w / aspect;
        return { x: (nw - w) / 2, y: (nh - h) / 2, w, h };
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const img = e.currentTarget;
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        setFailed(false);
        setNatural({ w: nw, h: nh });
        setDisplayWidth(img.clientWidth);

        if (initial && initial.right > initial.left && initial.down > initial.up) {
            const x = clamp(initial.left, 0, nw - 1);
            const y = clamp(initial.up, 0, nh - 1);
            setBox({
                x,
                y,
                w: clamp(initial.right - initial.left, 1, nw - x),
                h: clamp(initial.down - initial.up, 1, nh - y),
            });
        } else {
            setBox(largestCentredBox(nw, nh));
        }
    };

    // Keep the display scale in sync when the layout (not the crop) resizes
    useEffect(() => {
        const img = imgRef.current;
        if (!img || typeof ResizeObserver === 'undefined') return;
        const observer = new ResizeObserver(() => setDisplayWidth(img.clientWidth));
        observer.observe(img);
        return () => observer.disconnect();
    }, []);

    // Report through a ref so parents may pass inline callbacks without re-emit loops
    const onChangeRef = useRef(onChange);
    useEffect(() => {
        onChangeRef.current = onChange;
    });
    useEffect(() => {
        if (!box) return;
        onChangeRef.current({
            filled: true,
            left: Math.round(box.x),
            up: Math.round(box.y),
            right: Math.round(box.x + box.w),
            down: Math.round(box.y + box.h),
        });
    }, [box]);

    const pointerToOriginal = (e: React.PointerEvent) => {
        const rect = imgRef.current!.getBoundingClientRect();
        return { x: (e.clientX - rect.left) / scale, y: (e.clientY - rect.top) / scale };
    };

    const beginDrag = (e: React.PointerEvent, state: DragState) => {
        e.preventDefault();
        e.stopPropagation();
        stageRef.current?.setPointerCapture(e.pointerId);
        dragRef.current = state;
        setInteracting(true);
    };

    const handleWindowPointerDown = (e: React.PointerEvent) => {
        if (!ready) return;
        const p = pointerToOriginal(e);
        beginDrag(e, { mode: 'move', grabX: p.x - box.x, grabY: p.y - box.y });
    };

    const handleGripPointerDown = (e: React.PointerEvent, corner: Corner) => {
        if (!ready) return;
        const anchorX = corner === 'nw' || corner === 'sw' ? box.x + box.w : box.x;
        const anchorY = corner === 'nw' || corner === 'ne' ? box.y + box.h : box.y;
        beginDrag(e, { mode: corner, anchorX, anchorY });
    };

    const handlePointerMove = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        if (!drag || !ready) return;
        const p = pointerToOriginal(e);

        if (drag.mode === 'move') {
            setBox(prev => prev && {
                ...prev,
                x: clamp(p.x - drag.grabX, 0, natural.w - prev.w),
                y: clamp(p.y - drag.grabY, 0, natural.h - prev.h),
            });
            return;
        }

        const dirX = drag.mode === 'ne' || drag.mode === 'se' ? 1 : -1;
        const dirY = drag.mode === 'sw' || drag.mode === 'se' ? 1 : -1;
        const availW = dirX > 0 ? natural.w - drag.anchorX : drag.anchorX;
        const availH = dirY > 0 ? natural.h - drag.anchorY : drag.anchorY;

        const desiredW = Math.max((p.x - drag.anchorX) * dirX, ((p.y - drag.anchorY) * dirY) * aspect);
        const maxHere = Math.min(availW, availH * aspect);
        const w = clamp(desiredW, Math.min(minW, maxHere), maxHere);
        const h = w / aspect;

        setBox({
            x: dirX > 0 ? drag.anchorX : drag.anchorX - w,
            y: dirY > 0 ? drag.anchorY : drag.anchorY - h,
            w,
            h,
        });
    };

    const endDrag = (e: React.PointerEvent) => {
        if (!dragRef.current) return;
        dragRef.current = null;
        setInteracting(false);
        if (stageRef.current?.hasPointerCapture(e.pointerId)) {
            stageRef.current.releasePointerCapture(e.pointerId);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (!ready) return;
        const step = (e.shiftKey ? 10 : 1) / scale;
        const moves: Record<string, [number, number]> = {
            ArrowLeft: [-step, 0],
            ArrowRight: [step, 0],
            ArrowUp: [0, -step],
            ArrowDown: [0, step],
        };
        const move = moves[e.key];
        if (!move) return;
        e.preventDefault();
        setBox(prev => prev && {
            ...prev,
            x: clamp(prev.x + move[0], 0, natural.w - prev.w),
            y: clamp(prev.y + move[1], 0, natural.h - prev.h),
        });
    };

    const handleReset = () => setBox(largestCentredBox(natural.w, natural.h));

    if (failed) {
        return (
            <p style={{ color: 'var(--st-danger)', fontSize: '0.85rem', margin: '0.5rem 0' }}>
                Obrázok nie je dostupný. Obnovte stránku a skúste to znova.
            </p>
        );
    }

    const grips: { corner: Corner; style: React.CSSProperties; cursor: string }[] = ready ? [
        { corner: 'nw', cursor: 'nwse-resize', style: { top: -7, left: -7, borderTop: `3px solid ${EDGE}`, borderLeft: `3px solid ${EDGE}`, borderTopLeftRadius: 4 } },
        { corner: 'ne', cursor: 'nesw-resize', style: { top: -7, right: -7, borderTop: `3px solid ${EDGE}`, borderRight: `3px solid ${EDGE}`, borderTopRightRadius: 4 } },
        { corner: 'sw', cursor: 'nesw-resize', style: { bottom: -7, left: -7, borderBottom: `3px solid ${EDGE}`, borderLeft: `3px solid ${EDGE}`, borderBottomLeftRadius: 4 } },
        { corner: 'se', cursor: 'nwse-resize', style: { bottom: -7, right: -7, borderBottom: `3px solid ${EDGE}`, borderRight: `3px solid ${EDGE}`, borderBottomRightRadius: 4 } },
    ] : [];

    return (
        <div>
            <div
                ref={stageRef}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                style={{
                    position: 'relative',
                    width: 'fit-content',
                    maxWidth: '100%',
                    minHeight: ready ? undefined : 120,
                    margin: '0 auto',
                    overflow: 'hidden',
                    borderRadius: 'var(--st-radius-ctl)',
                    border: '1px solid var(--st-line)',
                    backgroundColor: 'var(--st-ink)',
                    touchAction: 'none',
                    userSelect: 'none',
                }}
            >
                {!ready && (
                    <span style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'var(--st-text-faint)',
                        fontSize: '0.8rem',
                    }}>
                        Načítavam obrázok...
                    </span>
                )}

                <img
                    ref={imgRef}
                    src={imageUrl}
                    alt={imageAlt}
                    draggable={false}
                    onLoad={handleImageLoad}
                    onError={() => setFailed(true)}
                    style={{ display: 'block', maxWidth: '100%', maxHeight: '58vh', position: 'relative' }}
                />

                {ready && (
                    <div
                        role="group"
                        aria-label="Výrez obrázka"
                        tabIndex={0}
                        onPointerDown={handleWindowPointerDown}
                        onDoubleClick={handleReset}
                        onKeyDown={handleKeyDown}
                        style={{
                            position: 'absolute',
                            left: box.x * scale,
                            top: box.y * scale,
                            width: box.w * scale,
                            height: box.h * scale,
                            boxSizing: 'border-box',
                            border: `1.5px solid ${EDGE}`,
                            borderRadius: 2,
                            boxShadow: `0 0 0 9999px ${VEIL}`,
                            cursor: 'move',
                        }}
                    >
                        {/* Rule-of-thirds guides, visible while the window is being pulled */}
                        <div style={{ position: 'absolute', inset: 0, opacity: interacting ? 1 : 0, transition: 'opacity 0.18s ease', pointerEvents: 'none' }}>
                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: '33.333%', width: 1, backgroundColor: 'rgba(255,255,255,0.45)' }} />
                            <div style={{ position: 'absolute', top: 0, bottom: 0, left: '66.666%', width: 1, backgroundColor: 'rgba(255,255,255,0.45)' }} />
                            <div style={{ position: 'absolute', left: 0, right: 0, top: '33.333%', height: 1, backgroundColor: 'rgba(255,255,255,0.45)' }} />
                            <div style={{ position: 'absolute', left: 0, right: 0, top: '66.666%', height: 1, backgroundColor: 'rgba(255,255,255,0.45)' }} />
                        </div>

                        {grips.map(({ corner, style, cursor }) => (
                            <div
                                key={corner}
                                onPointerDown={e => handleGripPointerDown(e, corner)}
                                style={{
                                    position: 'absolute',
                                    width: 22,
                                    height: 22,
                                    cursor,
                                    filter: 'drop-shadow(0 0 2px rgba(24,24,27,0.6))',
                                    touchAction: 'none',
                                    ...style,
                                }}
                            />
                        ))}
                    </div>
                )}

                {ready && (
                    <div style={{
                        position: 'absolute',
                        right: 8,
                        bottom: 8,
                        pointerEvents: 'none',
                        fontFamily: 'var(--st-font-mono)',
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                        color: '#fff',
                        backgroundColor: sourceTooSmall ? 'var(--st-danger)' : 'rgba(24, 24, 27, 0.78)',
                        padding: '0.15rem 0.5rem',
                        borderRadius: 6,
                    }}>
                        {Math.round(box.w)} × {Math.round(box.h)} px
                    </div>
                )}
            </div>

            {sourceTooSmall && (
                <p style={{ color: 'var(--st-danger)', fontSize: '0.78rem', margin: '0.4rem 0 0' }}>
                    Obrázok má len {natural.w} × {natural.h} px — výrez potrebuje aspoň {minWidth} × {Math.round(minWidth / aspect)} px. Zvoľte väčší obrázok.
                </p>
            )}
        </div>
    );
};

export default CropBox;
